import React, { FC } from 'react';
import { useForm } from 'react-hook-form';
import { replace } from '@aarhus-university/au-lib-react-components/src/lib/helpers';
import { useLangContext } from '@aarhus-university/au-lib-react-components/src/lib/context';
import { getContextBasedHeadline } from '../../lib/helpers';
import ReceiptComponent from './ReceiptComponent';
import ErrorComponent from '../common/ErrorComponent';
import { useUserSignupByLdapMutation } from '../../services/api';
import { useRegistrationContext } from '../../lib/context';
import { useTranslationsContext } from '../../lib/translation';

const CreateAUIDComponent: FC = () => {
  const {
    register, handleSubmit, formState: { errors },
  } = useForm<IUserSignupByLdapPayload>();

  const lang = useLangContext();
  const translations = useTranslationsContext();
  const context = useRegistrationContext();
  const [userSignupByLdap, response] = useUserSignupByLdapMutation();
  const { data: userSignupByLdapResult, error, isLoading: uiBusy } = response;

  const onSubmit = (data: IUserSignupByLdapPayload) => {
    userSignupByLdap({
      username: data.username,
      password: data.password,
      context: context?.key as string,
    });
  };

  if (!context) {
    return null;
  }

  if (userSignupByLdapResult && userSignupByLdapResult.result !== 'InvalidCredentials' && userSignupByLdapResult.result !== 'Error') {
    return (
      <ReceiptComponent
        confirmDetails={userSignupByLdapResult}
      />
    );
  }

  let buttonClass = 'button';
  if (uiBusy) {
    buttonClass = `${buttonClass} button--processing`;
  }

  if (error != null) {
    buttonClass = `${buttonClass} visually-disabled`;
  }

  return (
    <>
      <div className="page__content__block">
        <div className="text--stamp">{getContextBasedHeadline(lang, context as ISignupContext)}</div>
        <h1>{translations.loginUsingAu}</h1>
        <form className="form" noValidate onSubmit={handleSubmit(onSubmit)}>
          <div className={errors.username ? 'form__field form-info--error' : 'form__field'}>
            <label htmlFor="username">
              {translations.username}
              {errors.username
                && (
                  <span className="form-info__message">
                    {errors.username.message}
                  </span>
                )}
              <input
                type="text"
                id="username"
                {...register('username', {
                  required: {
                    value: true,
                    message: translations.usernameIsRequired,
                  },
                  pattern: {
                    value: /^au\d+$/,
                    message: translations.usernameMustBeValidAuid,
                  },
                })}
                disabled={uiBusy}
              />
            </label>
          </div>
          <div className={(errors.password || (userSignupByLdapResult && userSignupByLdapResult.result === 'InvalidCredentials')) ? 'form__field form-info--error' : 'form__field'}>
            <label htmlFor="password">
              {translations.password}
              {errors.password
                && (
                  <span className="form-info__message">
                    {translations.passwordIsRequired}
                  </span>
                )}
              {(userSignupByLdapResult && userSignupByLdapResult.result === 'InvalidCredentials') && (
                <span className="form-info__message">
                  {translations.invalidUsernameOrPassword}
                </span>
              )}
              <input
                type="password"
                id="password"
                {...register('password', {
                  required: true,
                })}
                disabled={uiBusy}
              />
            </label>
          </div>
          <a className="a--text-link" href={`https://adgang.au.dk/${lang}`}>{translations.forgotLink}</a>
          <div className="submit button-container">
            <button type="submit" className={buttonClass} disabled={uiBusy || error != null}>{translations.createAccess}</button>
          </div>
        </form>
      </div>
      {
        (userSignupByLdapResult && userSignupByLdapResult.result === 'Error') && (
          <div className="page__content__block">
            <ErrorComponent
              header="Error"
              text="Error during LDAP credential signup"
            />
          </div>
        )
      }
      {
        error && (
          <div className="page__content__block">
            <ErrorComponent
              header={translations.errorHeader}
              text={replace(translations.errorBody, ['context'], [context.key])}
            />
          </div>
        )
      }
    </>
  );
};

CreateAUIDComponent.displayName = 'CreateAUIDComponent';
export default CreateAUIDComponent;
